import { Money } from '@principle-theorem/accounting';
import {
  isMultiTreatmentConfiguration,
  type IDepositAmount,
  type IPractice,
  type IStaffer,
  type ITreatmentStep,
  type ITreatmentTemplate,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  toNamedDocument,
  type INamedDocument,
  type IReffable,
  type WithRef,
} from '@principle-theorem/shared';
import { AppointmentTreatmentPlanAssociator } from '../appointment-store/appointment-treatment-plan-associator';
import { TreatmentPlan } from '../clinical-charting/treatment/treatment-plan';
import { TreatmentStep } from '../clinical-charting/treatment/treatment-step';
import { TreatmentTemplate } from '../clinical-charting/treatment/treatment-template';

export interface IResolveOnlineTreatmentCostOptions {
  readonly template: WithRef<ITreatmentTemplate>;
  readonly practice: WithRef<IPractice>;
  readonly practitioner?: INamedDocument<IStaffer>;
  readonly templatePriceOverrideCents?: number;
  readonly treatmentStep?: ITreatmentStep;
}

/** Owns deposit amount calculation and treatment-cost resolution. */
export class DepositAmount {
  /**
   * Resolves an online treatment cost with staff-booking pricing semantics so
   * both booking paths calculate deposits from the same monetary basis.
   */
  static async resolveOnlineTreatmentCost({
    template,
    practice,
    practitioner,
    templatePriceOverrideCents,
    treatmentStep,
  }: IResolveOnlineTreatmentCostOptions): Promise<number | undefined> {
    const resolvedPractitioner =
      practitioner ?? template.implementedBy?.[0]?.staffer;
    const defaultFeeScheduleRef = practice.settings.defaultFeeSchedule;
    if (resolvedPractitioner && defaultFeeScheduleRef) {
      const feeSchedule = await Firestore.getDoc(defaultFeeScheduleRef);
      const templateWithStep = TreatmentPlan.getTreatmentPlanFromTemplate(
        template,
        resolvedPractitioner,
        toNamedDocument(practice)
      );
      const pricedTemplate =
        await AppointmentTreatmentPlanAssociator.getTreatmentPlanStepsPair(
          templateWithStep,
          feeSchedule,
          [],
          resolvedPractitioner
        );
      const computedTreatmentPrice = pricedTemplate.steps[0]
        ? TreatmentStep.price(pricedTemplate.steps[0])
        : 0;
      if (computedTreatmentPrice > 0) {
        return Money.toCents(computedTreatmentPrice);
      }
    }

    if (
      templatePriceOverrideCents !== undefined &&
      templatePriceOverrideCents > 0
    ) {
      return Math.round(templatePriceOverrideCents);
    }

    const treatmentStepPrice = treatmentStep
      ? TreatmentStep.price(treatmentStep)
      : 0;
    return treatmentStepPrice > 0
      ? Money.toCents(treatmentStepPrice)
      : undefined;
  }

  /**
   * The treatment configuration ids a booking would chart, one entry per unit,
   * so per-configuration deposits can be summed by quantity. Prefers the
   * practitioner's own implementation and falls back to the template default,
   * matching how the booking is charted at submit time.
   */
  static async resolveTreatmentConfigurationIds(
    template: WithRef<ITreatmentTemplate>,
    practitioner?: IReffable<IStaffer>
  ): Promise<readonly string[]> {
    const implementor =
      practitioner && template.implementedBy?.length
        ? TreatmentTemplate.getImplementor(template, practitioner)
        : undefined;
    const treatment =
      implementor?.treatment ?? TreatmentTemplate.getTreatment(template);
    if (!treatment) {
      return [];
    }

    const configuration = await Firestore.getDoc(treatment.ref);
    if (!isMultiTreatmentConfiguration(configuration)) {
      return [configuration.ref.id];
    }

    return (
      configuration.steps[0]?.treatments.flatMap((item) =>
        Array.from({ length: item.quantity }, () => item.ref.id)
      ) ?? []
    );
  }

  /** Calculates a payable amount while preventing deposits exceeding treatment value. */
  static calculate(
    depositConfig: IDepositAmount,
    treatmentCost: number
  ): number {
    if (depositConfig.amountCents < 0) {
      throw new Error('Deposit value cannot be negative');
    }

    // The clamp only stops a deposit exceeding the treatment value, so it must
    // not apply when the cost is unknown - that would zero every online deposit.
    return treatmentCost > 0
      ? Math.min(depositConfig.amountCents, treatmentCost)
      : depositConfig.amountCents;
  }
}
